import React, { useState, useEffect, useCallback } from "react";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Play, Save, Download, Trash2, FileText, Plus, X } from "lucide-react";
import {
  COLORS, API_BASE, apiFetch, cardStyle, inputStyle, labelStyle, btnPrimary,
  btnSecondary, thStyle, tdStyle, mono, tableWrap,
  PageHeader, LoadingSpinner, EmptyState,
} from "../shared/ui";
import { fechaLocalISO } from "../shared/fechas";
import { podarOrden } from "./podarOrden";
import { filasParaLinea } from "./filasParaLinea";
import { CATALOGO_FILTROS, camposParaFuente, filtroAFila, podarFiltros } from "./filtros";

// Catálogo espejo del backend (app/services/report_builder.py)
const METRICAS = {
  ventas: [
    { id: "ingresos", label: "Ingresos $" }, { id: "unidades_vendidas", label: "Unidades vendidas" },
    { id: "costo_ventas", label: "Costo de ventas $" }, { id: "margen_bruto", label: "Margen bruto $" },
    { id: "margen_pct", label: "Margen %" }, { id: "num_articulos", label: "Nº artículos" },
  ],
  inventario: [
    { id: "stock_unidades", label: "Stock (unidades)" }, { id: "stock_valor_costo", label: "Stock a costo $" },
    { id: "stock_valor_precio", label: "Stock a precio $" }, { id: "dias_sin_venta_prom", label: "Días sin venta (prom., solo con venta)" },
    { id: "num_quiebres", label: "SKUs en cero" }, { id: "num_articulos", label: "Nº artículos" },
  ],
};
const DIMENSIONES = {
  ventas: [
    { id: "bodega", label: "Bodega" }, { id: "articulo", label: "Artículo" },
    { id: "clasificador", label: "Clasificador" }, { id: "dia", label: "Día" },
    { id: "semana", label: "Semana" }, { id: "mes", label: "Mes" },
    { id: "trimestre", label: "Trimestre" }, { id: "anio", label: "Año" },
  ],
  inventario: [
    { id: "bodega", label: "Bodega" }, { id: "articulo", label: "Artículo" },
    { id: "clasificador", label: "Clasificador" }, { id: "clasificacion_rotacion", label: "Clasif. rotación" },
  ],
};

const DEF_INICIAL = { fuente: "ventas", metricas: ["ingresos"], dimensiones: ["bodega"], filtros: [], periodo: { dias: 90 }, limite: 500 };

async function descargarCSV(defn, token) {
  const res = await fetch(`${API_BASE}/bi/reportes/ejecutar/csv`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify(defn),
  });
  if (!res.ok) throw new Error("Error al exportar CSV");
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `reporte_bi_${fechaLocalISO()}.csv`;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function ReportBuilderPage({ token }) {
  const [guardados, setGuardados] = useState(null);
  const [defn, setDefn] = useState(DEF_INICIAL);
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState("");
  const [ejecutando, setEjecutando] = useState(false);
  const [nombreGuardar, setNombreGuardar] = useState("");
  const [filas, setFilas] = useState([]);
  const [bodegas, setBodegas] = useState([]);
  const [tiposClasif, setTiposClasif] = useState([]);

  useEffect(() => {
    apiFetch("/bodegas", {}, token).then(r => setBodegas(r.data || r || [])).catch(() => {});
    apiFetch("/clasificadores", {}, token).then(r => {
      const lista = Array.isArray(r) ? r : (r.data || []);
      const principal = lista.find(c => c.principal === "S") || lista[0];
      setTiposClasif(principal?.tipos || []);
    }).catch(() => {});
  }, [token]);

  // Las filas son la fuente de verdad de los filtros; defn.filtros se recalcula aquí
  const actualizarFilas = (nuevas) => {
    setFilas(nuevas);
    setDefn(d => ({ ...d, filtros: podarFiltros(nuevas) }));
  };

  const cargarGuardados = useCallback(() => {
    apiFetch("/bi/reportes", {}, token)
      .then(r => setGuardados(r.data))
      .catch(e => {
        setError(`No se pudieron cargar los reportes guardados: ${e.message}`);
        setGuardados([]);
      });
  }, [token]);
  useEffect(() => { cargarGuardados(); }, [cargarGuardados]);

  const setFuente = (fuente) => {
    setDefn({ ...DEF_INICIAL, fuente, metricas: [METRICAS[fuente][0].id], dimensiones: ["bodega"] });
    setFilas([]);
    setResultado(null);
  };
  const toggle = (lista, id, max) => {
    const nueva = lista.includes(id) ? lista.filter(x => x !== id) : [...lista, id].slice(-max);
    return nueva;
  };

  const opcionesDe = (campo, seleccion) => {
    let base;
    if (campo === "bodega") base = bodegas.map(b => ({ v: String(b.id_bodega), l: `${b.id_bodega} — ${b.descripcion || b.nombre || ""}` }));
    else if (campo === "clasificador") base = tiposClasif.map(t => ({ v: String(t.id_tipo), l: `${t.id_tipo} — ${t.nombre_tipo || ""}` }));
    else base = (CATALOGO_FILTROS[campo]?.opciones || []).map(o => ({ v: o, l: o }));
    const conocidos = new Set(base.map(o => o.v));
    (seleccion || []).forEach(v => { if (!conocidos.has(v)) base.push({ v, l: v }); });
    return base;
  };
  const filaNueva = (campo) => {
    const tipo = CATALOGO_FILTROS[campo].tipo;
    if (tipo === "numero") return { campo, operador: ">=", texto: "" };
    if (tipo === "multi") return opcionesDe(campo, []).length ? { campo, valores: [] } : { campo, texto: "", degradado: true };
    return { campo, texto: "" };
  };
  const setFila = (i, cambios) => actualizarFilas(filas.map((f, j) => (j === i ? { ...f, ...cambios } : f)));
  const quitarFila = (i) => actualizarFilas(filas.filter((_, j) => j !== i));

  const ejecutar = async (definicion = defn) => {
    setEjecutando(true); setError("");
    try {
      const r = await apiFetch("/bi/reportes/ejecutar", { method: "POST", body: JSON.stringify(definicion) }, token);
      setResultado(r);
    } catch (e) { setError(e.message); setResultado(null); }
    setEjecutando(false);
  };

  const guardar = async () => {
    if (!nombreGuardar.trim()) return;
    try {
      await apiFetch("/bi/reportes", { method: "POST", body: JSON.stringify({ nombre: nombreGuardar.trim(), definicion: defn }) }, token);
      setNombreGuardar(""); cargarGuardados();
    } catch (e) { setError(e.message); }
  };

  const borrar = async (id) => {
    try { await apiFetch(`/bi/reportes/${id}`, { method: "DELETE" }, token); cargarGuardados(); }
    catch (e) { setError(e.message); }
  };

  if (guardados === null) return <LoadingSpinner />;
  const dimensiones = resultado?.definicion?.dimensiones || [];
  const filasLinea = resultado ? filasParaLinea(resultado.filas, dimensiones) : null;
  const metrica0 = resultado?.definicion?.metricas?.[0];

  return (
    <div>
      <PageHeader title="Reportes dinámicos" subtitle="Construya consultas sobre ventas e inventario, guárdelas y expórtelas" />
      <div style={{ display: "grid", gridTemplateColumns: "260px 1fr", gap: 16, alignItems: "start" }}>

        {/* Catálogo */}
        <div style={{ ...cardStyle, padding: 14 }}>
          <h3 style={{ fontSize: 13, fontWeight: 700, color: COLORS.text, margin: "0 0 10px" }}>Reportes guardados</h3>
          {guardados.length === 0 && <p style={{ fontSize: 12, color: COLORS.textDim }}>Sin reportes</p>}
          {guardados.map(r => (
            <div key={r.id_reporte} style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 4px", borderBottom: `1px solid ${COLORS.border}` }}>
              <FileText size={13} color={r.es_sistema ? COLORS.textDim : COLORS.primary} style={{ flexShrink: 0 }} />
              <button onClick={() => { setDefn(r.definicion); setFilas((r.definicion.filtros || []).map(filtroAFila)); ejecutar(r.definicion); }}
                title={r.descripcion || r.nombre}
                style={{ flex: 1, textAlign: "left", background: "none", border: "none", cursor: "pointer", fontSize: 12, color: COLORS.textSecond, fontFamily: "'DM Sans', sans-serif", padding: 0 }}>
                {r.nombre}
              </button>
              {!r.es_sistema && (
                <button onClick={() => borrar(r.id_reporte)} style={{ background: "none", border: "none", cursor: "pointer", padding: 2 }}>
                  <Trash2 size={12} color={COLORS.danger} />
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Constructor + resultados */}
        <div>
          <div style={{ ...cardStyle, marginBottom: 16 }}>
            <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
              <div>
                <label style={labelStyle}>Fuente</label>
                <select value={defn.fuente} onChange={e => setFuente(e.target.value)} style={{ ...inputStyle, width: 150 }}>
                  <option value="ventas">Ventas</option>
                  <option value="inventario">Inventario</option>
                </select>
              </div>
              <div style={{ flex: 1, minWidth: 220 }}>
                <label style={labelStyle}>Métricas (máx. 6)</label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {METRICAS[defn.fuente].map(m => (
                    <button key={m.id} onClick={() => setDefn(d => podarOrden({ ...d, metricas: toggle(d.metricas, m.id, 6) }))}
                      style={{ padding: "5px 10px", borderRadius: 14, fontSize: 11, fontWeight: 600, cursor: "pointer",
                        border: `1px solid ${defn.metricas.includes(m.id) ? COLORS.primary : COLORS.border}`,
                        background: defn.metricas.includes(m.id) ? COLORS.primarySoft : COLORS.surface,
                        color: defn.metricas.includes(m.id) ? COLORS.primary : COLORS.textMuted }}>
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ flex: 1, minWidth: 220 }}>
                <label style={labelStyle}>Agrupar por (máx. 3)</label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {DIMENSIONES[defn.fuente].map(m => (
                    <button key={m.id} onClick={() => setDefn(d => podarOrden({ ...d, dimensiones: toggle(d.dimensiones, m.id, 3) }))}
                      style={{ padding: "5px 10px", borderRadius: 14, fontSize: 11, fontWeight: 600, cursor: "pointer",
                        border: `1px solid ${defn.dimensiones.includes(m.id) ? COLORS.accent : COLORS.border}`,
                        background: defn.dimensiones.includes(m.id) ? COLORS.primarySoft : COLORS.surface,
                        color: defn.dimensiones.includes(m.id) ? COLORS.accent : COLORS.textMuted }}>
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>
              {defn.fuente === "ventas" && (
                <div>
                  <label style={labelStyle}>Período (días)</label>
                  <select value={defn.periodo.dias || 90} onChange={e => setDefn(d => ({ ...d, periodo: { dias: Number(e.target.value) } }))} style={{ ...inputStyle, width: 120 }}>
                    {[30, 60, 90, 180, 365].map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
              )}
            </div>
            <div style={{ marginTop: 14 }}>
              <label style={labelStyle}>Filtros (máx. 8)</label>
              {filas.map((fila, i) => {
                const def = CATALOGO_FILTROS[fila.campo];
                const tipo = fila.degradado || !def ? "texto" : def.tipo;
                return (
                  <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 6, flexWrap: "wrap" }}>
                    <select value={fila.campo} onChange={e => setFila(i, filaNueva(e.target.value))} style={{ ...inputStyle, width: 190 }}>
                      {!def && <option value={fila.campo}>{fila.campo}</option>}
                      {camposParaFuente(defn.fuente).map(c => <option key={c} value={c}>{CATALOGO_FILTROS[c].label}</option>)}
                    </select>
                    {tipo === "multi" && (
                      <select multiple value={fila.valores || []} size={Math.min(4, Math.max(2, opcionesDe(fila.campo, fila.valores).length))}
                        onChange={e => setFila(i, { valores: Array.from(e.target.selectedOptions).map(o => o.value) })}
                        style={{ ...inputStyle, minWidth: 230, height: "auto" }}>
                        {opcionesDe(fila.campo, fila.valores).map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                      </select>
                    )}
                    {tipo === "numero" && (
                      <>
                        <select value={fila.operador || ">="} onChange={e => setFila(i, { operador: e.target.value })} style={{ ...inputStyle, width: 70 }}>
                          <option value=">=">≥</option>
                          <option value="<=">≤</option>
                        </select>
                        <input type="number" value={fila.texto ?? ""} onChange={e => setFila(i, { texto: e.target.value })}
                          placeholder="días" style={{ ...inputStyle, width: 110 }} />
                      </>
                    )}
                    {tipo === "texto" && (
                      <input value={fila.texto ?? ""} onChange={e => setFila(i, { texto: e.target.value })}
                        placeholder={fila.degradado ? "valor(es), coma" : "códigos separados por coma"} style={{ ...inputStyle, minWidth: 230 }} />
                    )}
                    <button onClick={() => quitarFila(i)} title="Quitar filtro"
                      style={{ background: "none", border: "none", cursor: "pointer", padding: 6 }}>
                      <X size={14} color={COLORS.danger} />
                    </button>
                  </div>
                );
              })}
              <button onClick={() => actualizarFilas([...filas, filaNueva("bodega")])} disabled={filas.length >= 8}
                style={{ ...btnSecondary, padding: "5px 10px", fontSize: 11, opacity: filas.length >= 8 ? 0.5 : 1 }}>
                <Plus size={12} /> Agregar filtro
              </button>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 16, alignItems: "center", flexWrap: "wrap" }}>
              <button onClick={() => ejecutar()} disabled={ejecutando || defn.metricas.length === 0} style={btnPrimary}>
                <Play size={14} /> {ejecutando ? "Ejecutando..." : "Ejecutar"}
              </button>
              {resultado && (
                <button onClick={() => descargarCSV(defn, token).catch(e => setError(e.message))} style={btnSecondary}>
                  <Download size={14} /> CSV
                </button>
              )}
              <input placeholder="Nombre para guardar…" value={nombreGuardar} onChange={e => setNombreGuardar(e.target.value)}
                style={{ ...inputStyle, width: 200, padding: "8px 12px" }} />
              <button onClick={guardar} disabled={!nombreGuardar.trim()} style={btnSecondary}>
                <Save size={14} /> Guardar
              </button>
            </div>
            {error && <div style={{ marginTop: 10, padding: "8px 12px", background: COLORS.dangerSoft, border: "1px solid #FECACA", borderRadius: 7, color: COLORS.danger, fontSize: 12 }}>{error}</div>}
          </div>

          {resultado && resultado.filas.length === 0 && <EmptyState message="El reporte no devolvió datos" />}

          {resultado && resultado.filas.length > 0 && (
            <>
              {metrica0 && resultado.definicion.dimensiones.length >= 1 && (
                <div style={{ ...cardStyle, marginBottom: 16 }}>
                  <ResponsiveContainer width="100%" height={220}>
                    {filasLinea ? (
                      <LineChart data={filasLinea}>
                        <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
                        <XAxis dataKey={dimensiones[0]} tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 10 }} />
                        <Tooltip />
                        <Line type="monotone" dataKey={metrica0} stroke={COLORS.primary} dot={false} />
                      </LineChart>
                    ) : (
                      <BarChart data={resultado.filas.slice(0, 20)}>
                        <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
                        <XAxis dataKey={f => dimensiones.map(d => f[d]).join(" · ")} tick={{ fontSize: 9 }} interval={0} angle={-25} textAnchor="end" height={70} />
                        <YAxis tick={{ fontSize: 10 }} />
                        <Tooltip />
                        <Bar dataKey={metrica0} fill={COLORS.primary} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    )}
                  </ResponsiveContainer>
                </div>
              )}
              <div style={tableWrap}>
                <div style={{ maxHeight: 420, overflowY: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead><tr>{resultado.columnas.map(c => <th key={c.campo} style={thStyle}>{c.campo.replace(/_/g, " ")}</th>)}</tr></thead>
                    <tbody>
                      {resultado.filas.map((f, i) => (
                        <tr key={i}>
                          {resultado.columnas.map(c => (
                            <td key={c.campo} style={{ ...tdStyle, ...(c.tipo === "metrica" ? mono : {}) }}>
                              {c.tipo === "metrica" && f[c.campo] != null
                                ? Number(f[c.campo]).toLocaleString("es", { maximumFractionDigits: 2 })
                                : String(f[c.campo] ?? "—")}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ padding: "8px 16px", fontSize: 11, color: COLORS.textDim, borderTop: `1px solid ${COLORS.border}` }}>
                  {resultado.total} filas
                  {resultado.truncado && (
                    <span style={{ color: COLORS.warning, marginLeft: 8 }}>
                      · el reporte llegó al límite de {resultado.total}: hay más filas; suba el límite o agregue filtros
                    </span>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
